import {
	computed, observable, makeObservable,
} from 'mobx';
import { Axios } from '../utilities/network';
import { isWeb } from '../utilities/platform';
import RootStore from './root-store';
import { GetLocalStorage, SetLocalStorage } from '../utilities/cryptography';
import { USER_INFO } from '../constants/storage-keys';
import { navigate } from 'gatsby';
import { CURRENT_PLATFORM } from '../constants/settings';
import { ClinicMemberStatus, ClinicStatus, Gender, PAGE_SIZE } from '../constants/options';
import { ProfileTemplate } from '../utilities/profile';
import { GetApiLang, GetLangKey, GetUserProfile, IsClinicOwner, IsWorkingInClinic, Language } from '../utilities/general';
import { FAxios } from '../utilities/file-network';
import { t } from 'i18next';

export default class ClinicStore {

	loading: boolean = false;

	//   loadingHistory: boolean = false;

	creating: boolean = false;

	updating: boolean = false;

	deleting: boolean = false;

	searchingPractitioners: boolean = false;

	searchingModality: boolean = false;

	updatingModality: boolean = false;

	packageLoading: boolean = false;
	generatingInvoice: boolean = false;

	searchingCompanies: boolean = false;

	searchingPaymentDevice: boolean = false;

	rootStore: RootStore;

	constructor(rootStore: RootStore) {
		makeObservable(this, {
			loading: observable,
			creating: observable,
			updating: observable,
			updatingModality: observable,
			searchClinicPractitioners: observable,
			searchingModality: observable,
			packageLoading: observable,
			generatingInvoice: observable,
			searchingCompanies: observable,
			searchingPaymentDevice: observable,
		});
		this.rootStore = rootStore;
	}

	searchClinics = async (skip: number, take: number = PAGE_SIZE, reqModel: any = {}, lang: Language = GetApiLang()) => {
		this.loading = true;
		try {
			const res = await Axios.post(`/api/Clinic/Search/${skip}/${take}?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};

	addClinic = async (reqModel: any, lang: Language = GetApiLang()) => {
		this.creating = true;
		try {
			const res = await Axios.post(`/api/Clinic?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.creating = false;
		}
	};

	getClinicById = async (id: number, lang: Language = GetApiLang()) => {
		const userInfo = GetUserProfile();
		this.loading = true;
		try {
			const res = await Axios.get(`/api/Clinic/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
			let clinic = res.data;
			console.log("userinfo", userInfo);
			const availableClinicIndex = userInfo.availableClinics.findIndex((c: any) => c.id == clinic.id);
			// Keep the cached clinic ownership data in sync for both existing and newly-added clinics.
			if (IsWorkingInClinic(clinic.clinicMemberStatus) && clinic.status !== ClinicStatus.CLOSED) {
				console.log("Clinic Condition 1");
				const cachedClinic = availableClinicIndex >= 0 ? userInfo.availableClinics[availableClinicIndex] : null;
				const availableClinic = {
					...cachedClinic,
					id: clinic.id,
					name: clinic.name,
					address: clinic.address,
					logo: clinic.logoSavedName,
					ownerId: clinic.ownerId,
					allOwners: clinic.allOwners ?? cachedClinic?.allOwners ?? [],
					isOwner: Boolean(clinic.isOwner) || IsClinicOwner(clinic, userInfo.id),
				};
				if (availableClinicIndex >= 0) userInfo.availableClinics[availableClinicIndex] = availableClinic;
				else userInfo.availableClinics.push(availableClinic);
				if (userInfo.currentClinicId == 0) userInfo.currentClinicId = clinic.id;
				SetLocalStorage(USER_INFO, userInfo);
			} else {
				// 如果当前工作诊所不在可用列表中，则将其从可用列表中移除
				if (!IsWorkingInClinic(clinic.clinicMemberStatus)) {
					console.log("Condition 2", userInfo.currentClinicId);
					userInfo.availableClinics = userInfo.availableClinics.filter((c: any) => c.id != clinic.id);
					if (userInfo.currentClinicId == clinic.id) userInfo.currentClinicId = userInfo.availableClinics[0] ? userInfo.availableClinics[0].id : 0;
					// navigate("/clinic")
					SetLocalStorage(USER_INFO, userInfo);
					// 如果当前工作诊所为空，则将当前工作诊所设置为第一个可用诊所
				} else if (userInfo.currentClinicId == 0) {
					console.log("Condition 3", userInfo.currentClinicId);
					userInfo.currentClinicId = clinic.id;
					SetLocalStorage(USER_INFO, userInfo);
				}
			}
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};

	updateClinic = async (reqModel: any, lang: Language = GetApiLang()) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Clinic?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			const userInfo = GetUserProfile();
			let idx = userInfo.availableClinics.findIndex((c: any) => c.id == reqModel.id);
			if (idx >= 0) userInfo.availableClinics[idx] = res.data;
			SetLocalStorage(USER_INFO, userInfo);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	uploadClinicLogo = async (reqModel: any) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Clinic/Logo?platformId=${CURRENT_PLATFORM}`, reqModel);
			const clinic = res.data;
			let userInfo = GetUserProfile();
			let idx = userInfo.availableClinics.findIndex((c: any) => c.id == reqModel.clinicId);
			userInfo.availableClinics[idx] = { ...userInfo.availableClinics[idx], logo: clinic.logoSavedName };
			SetLocalStorage(USER_INFO, userInfo);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	getGeneralTimeSlots = async (reqModel: any, lang: Language = GetApiLang()) => {
		this.loading = true;
		try {
			const res = await Axios.post(`/api/Clinic/Practitioner/GeneralTimeSlots?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};

	updateClinicMemberSettings = async (reqModel: any, lang: Language = GetApiLang()) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Clinic/Practitioner/WorkingSetting?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	searchClinicPractitioners = async (skip: number, take: number = PAGE_SIZE, reqModel: any = {}, lang: Language = GetApiLang()) => {
		this.searchingPractitioners = true;
		try {
			const res = await Axios.post(`/api/Clinic/Practitioner/Search/${skip}/${take}?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.searchingPractitioners = false;
		}
	};

	invitePractitionerToClinic = async (reqModel: any) => {
		this.creating = true;
		try {
			const res = await Axios.post(`/api/Clinic/Practitioner/Invite?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.creating = false;
		}
	};

	multiInvitePractitionerToClinic = async (reqModel: any) => {
		this.creating = true;
		try {
			const res = await Axios.post(`/api/Clinic/Practitioner/MultipleInvite/?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.creating = false;
		}
	};

	responseInvitePractitionerToClinic = async (reqModel: any, isApprove: boolean) => {
		this.updating = true;
		try {
			let apiUrl = isApprove ? `/api/Clinic/Practitioner/Approve?platformId=${CURRENT_PLATFORM}`
				: `/api/Clinic/Practitioner/Reject?platformId=${CURRENT_PLATFORM}`;
			const res = await Axios.put(apiUrl, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	getClinicMemberById = async (id: number, lang: Language = GetApiLang()) => {
		this.loading = true;
		try {
			const res = await Axios.get(`/api/Clinic/Practitioner/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};

	quitClinic = async (reqModel: any, lang: Language = GetApiLang()) => {
		this.updating = true;
		try {
			const res = await Axios.post(`/api/Clinic/Practitioner/Quit?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	responseInviteQuitClinic = async (reqModel: any, isApprove: boolean) => {
		this.updating = true;
		try {
			let apiUrl = isApprove ? `/api/Clinic/Practitioner/Quit/Approve?platformId=${CURRENT_PLATFORM}`
				: `/api/Clinic/Practitioner/Quit/Reject?platformId=${CURRENT_PLATFORM}`;
			const res = await Axios.put(apiUrl, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	togglePractitionerStatus = async (reqModel: { clinicId: number, practitionerId: number; }, isActive: boolean) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Clinic/Practitioner/Toggle/Active?isActive=${isActive}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	closeClinic = async (id: number, lang: Language = GetApiLang()) => {
		const userInfo = GetUserProfile();
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Clinic/Close/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
			userInfo.availableClinics = userInfo.availableClinics.filter((c: any) => c.id != id);
			if (userInfo.availableClinics.length > 0) userInfo.currentClinicId = userInfo.availableClinics[0].id;
			else userInfo.currentClinicId = 0;
			SetLocalStorage(USER_INFO, userInfo);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	getClinicMemeberInfo = async (clinicId: number, practitionerId: number, lang: Language = GetApiLang(), isloading: boolean = true) => {
		if (isloading) this.loading = true;
		try {
			const res = await Axios.get(`/api/Clinic/Practitioner/GeneralInfo/${clinicId}/${practitionerId}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			if (isloading) this.loading = false;
		}
	};

	// Resolved RBAC permission codes for ANOTHER practitioner in this clinic —
	// same response shape as getMyPermissions (a plain string[] of codes), just
	// scoped by clinicId/practitionerId instead of the signed-in session. Used
	// by the practitioner detail modal's "Permission Assignment" tab; viewable
	// by anyone who can open that modal, regardless of clinic.permission.update
	// (that permission only gates whether the role can be changed, not viewed).
	getPractitionerPermissions = async (clinicId: number, practitionerId: number) => {
		try {
			const res = await Axios.get(`/api/Clinic/Practitioner/Permissions/${clinicId}/${practitionerId}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		}
	};

	// Reassigns a practitioner's role in this clinic. Only meaningful for a
	// caller holding clinic.permission.update — the UI hides the role
	// selector/save affordance entirely otherwise.
	updatePractitionerPermissions = async (reqModel: { clinicId: number; practitionerId: number; roleId: number; }) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Clinic/Practitioner/Permissions?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	// Refreshes the signed-in user's own RBAC permissions for the current
	// clinic — called by PermissionProvider's poll (like packageStore's
	// getMemberPackagePermission) and by the 403 response interceptor
	// (network.ts) so a stale local permission cache self-corrects. De-duped
	// so concurrent callers (e.g. several 403s at once) don't all fire a request.
	fetchingMyPermissions: boolean = false;
	getMyPermissions = async () => {
		if (this.fetchingMyPermissions) return;
		this.fetchingMyPermissions = true;
		try {
			const res = await Axios.get(`/api/Clinic/Practitioner/Permissions?platformId=${CURRENT_PLATFORM}`);
			const newCodes: string[] = res.data ?? [];
			// Compare against whatever was cached for the currently-active clinic
			// (real or temp) BEFORE overwriting it — only notify when there was
			// already a known set to diverge from, so the very first fetch (after
			// login, or right as temp mode turns on) never falsely announces an
			// "update". Order-independent: this is a set of codes, not a sequence.
			const codesDiffer = (prev: string[]) => prev.length > 0 && (
				prev.length !== newCodes.length || !prev.every((c) => newCodes.includes(c))
			);
			// While clinic/info.tsx or clinic/products/index.tsx has a clinic-scoped
			// temp token active (UserStore.isTempPermissionActive), this request
			// rides on THAT token (see network.ts's interceptor) — the response
			// describes the TEMP clinic's permissions, not the real session's.
			// Writing it into the real permissionCodes would silently corrupt the
			// real session with a different clinic's data; route it into
			// tempPermissionCodes instead so HasPermission (which itself reads
			// whichever of the two is currently active) sees the right thing.
			if (this.rootStore.userStore.isTempPermissionActive) {
				const hasChanged = codesDiffer(this.rootStore.userStore.tempPermissionCodes ?? []);
				this.rootStore.userStore.tempPermissionCodes = newCodes;
				if (hasChanged) {
					this.rootStore.notify(t('CLINIC_WORK_PERMISSIONS_UPDATED'), 'info');
				}
			} else {
				const hasChanged = codesDiffer(this.rootStore.userStore.permissionCodes ?? []);
				this.rootStore.userStore.setPermissionCodes(newCodes);
				if (hasChanged) {
					this.rootStore.notify(t('CLINIC_WORK_PERMISSIONS_UPDATED'), 'info');
				}
			}
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.fetchingMyPermissions = false;
		}
	};

	deleteClinicApplication = async (id: number, lang: Language = GetApiLang()) => {
		this.deleting = true;
		try {
			const res = await Axios.delete(`/api/Clinic/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.deleting = false;
		}
	};

	updateClinicOwner = async (reqModel: any) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Clinic/Update/Owner?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	// Transfers primary ownership to another practitioner — replaces the old
	// createClinicOwner (which just ADDED a co-owner without removing the
	// current one, now unused/removed since the practitioner detail modal's
	// "Transfer Ownership" action uses this instead).
	transferClinicOwnership = async (reqModel: { clinicId: number; practitionerId: number; }) => {
		this.updating = true;
		try {
			const res = await Axios.put('/api/Clinic/Owner/Transfer', reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	searchModalityByClinicMember = async (practitionerId: number, clinicId: number) => {
		this.searchingModality = true;
		try {
			const res = await Axios.put(`/api/Clinic/Practitioner/Modality/Search/0/0?platformId=${CURRENT_PLATFORM}`, { practitionerId, clinicId });
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.searchingModality = false;
		}
	};

	//package


	createClinicPackage = async (reqModel: any, lang: Language = GetApiLang()) => {
		this.packageLoading = true;
		try {
			const res = await Axios.post(`/api/Clinic/Package?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.packageLoading = false;
		}
	};

	upgradeClinicPackage = async (reqModel: any, lang: Language = GetApiLang()) => {
		this.packageLoading = true;
		try {
			const res = await Axios.put(`/api/Clinic/Package/Upgrade?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.packageLoading = false;
		}
	};

	renewClinicPackage = async (reqModel: any, lang: Language = GetApiLang()) => {
		this.packageLoading = true;
		try {
			const res = await Axios.put(`/api/Clinic/Package/Renew?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.packageLoading = false;
		}
	};

	getClinicPackage = async (id: number) => {
		this.packageLoading = true;
		try {
			const res = await Axios.get(`/api/Clinic/Package/${id}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.packageLoading = false;
		}
	};

	searchClinicPackage = async (skip: number, take: number, reqModel: any = {}) => {
		this.packageLoading = true;
		try {
			const res = await Axios.post(`/api/Clinic/Package/Search/${skip}/${take}?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.packageLoading = false;
		}
	};

	allocateClinicMembersToPackage = async (reqModel: any) => {
		this.packageLoading = true;
		try {
			const res = await Axios.put(`/api/Clinic/Package/AllocateMember?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.packageLoading = false;
		}
	};

	createClinicMemberPackage = async (reqModel: any) => {
		this.packageLoading = true;
		try {
			const res = await Axios.post(`/api/Clinic/Practitioner/Package?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.packageLoading = false;
		}
	};

	updateClinicMemberPackage = async (reqModel: any) => {
		this.packageLoading = true;
		try {
			const res = await Axios.put(`/api/Clinic/Practitioner/Package?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.packageLoading = false;
		}
	};

	deleteClinicMemberPackage = async (id: number) => {
		this.packageLoading = true;
		try {
			const res = await Axios.delete(`/api/Clinic/Practitioner/Package/${id}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.packageLoading = false;
		}
	};

	generateClinicPackageInvoice = async (id: number) => {
		this.generatingInvoice = true;
		try {
			const res = await FAxios.get(`/api/Clinic/Package/Payment/Invoice/${id}?platformId=${CURRENT_PLATFORM}`, { responseType: 'blob' });
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.generatingInvoice = false;
		}
	};

	generateAddonInvoice = async (id: number) => {
		this.generatingInvoice = true;
		try {
			const res = await FAxios.get(`/api/Clinic/Package/Addon/Payment/Invoice/${id}?platformId=${CURRENT_PLATFORM}`, { responseType: 'blob' });
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.generatingInvoice = false;
		}
	};

	inviteNewUserToClinic = async (reqModel: any) => {
		this.creating = true;
		try {
			const res = await Axios.post(`/api/Clinic/Practitioner/Invite/Token?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.creating = false;
		}
	};

	searchInvitedNewUsers = async (skip: number, take: number, reqModel: any) => {
		this.loading = true;
		try {
			const res = await Axios.post(`/api/Clinic/Practitioner/Invite/Token/Search/${skip}/${take}?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};

	resendEmailToInvitedNewUsers = async (id: number) => {
		this.loading = true;
		try {
			const res = await Axios.post(`/api/Clinic/Practitioner/Invite/Token/ResendEmail/${id}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};


	deleteEmailInvite = async (id: number) => {
		this.loading = true;
		try {
			const res = await Axios.delete(`/api/Clinic/Practitioner/Invite/${id}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};

	updateClinicPractitionerModality = async (practitionerId: number, reqModel: any) => {
		this.updatingModality = true;
		try {
			const res = await Axios.put(`/api/Clinic/Practitioner/Modality?practitionerId=${practitionerId}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updatingModality = false;
		}
	};

	searchPractitionerModalityInsurance = async (skip: number, take: number = PAGE_SIZE, reqModel: any = {}) => {
		this.loading = true;
		try {
			const res = await Axios.post(`/api/Clinic/Practitioner/Modality/Insurance/Search/${skip}/${take}?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};

	createPractitionerModalityInsurance = async (reqModel: any) => {
		this.creating = true;
		try {
			const res = await Axios.post(`/api/Clinic/Practitioner/Modality/Insurance?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.creating = false;
		}
	};

	updatetPractitionerModalityInsurance = async (reqModel: any) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Clinic/Practitioner/Modality/Insurance?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	deletePractitionerModalityInsurance = async (id: number) => {
		this.deleting = true;
		try {
			const res = await Axios.delete(`/api/Clinic/Practitioner/Modality/Insurance/${id}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.deleting = false;
		}
	};

	togglePractitionerInsuranceActiveStatus = async (insuranceId: number, isActive: boolean) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Clinic/Practitioner/Modality/Insurance/${isActive ? "Active" : "Inactive"}/${insuranceId}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	searchInsuranceCompanies = async (reqModel: any = {}) => {
		this.searchingCompanies = true;
		try {
			const res = await Axios.post(`/api/Insurance/Company/Search/0/0?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.searchingCompanies = false;
		}
	};

	createClinicBaseProduct = async (reqModel: any) => {
		this.creating = true;
		try {
			const res = await Axios.post(`/api/Clinic/Product/Base?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.creating = false;
		}
	};

	updateClinicBaseProduct = async (reqModel: any) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Clinic/Product/Base?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	deleteClinicBaseProduct = async (id: number) => {
		this.deleting = true;
		try {
			const res = await Axios.delete(`/api/Clinic/Product/Base/${id}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.deleting = false;
		}
	};

	createClinicProduct = async (reqModel: any) => {
		this.creating = true;
		try {
			const res = await Axios.post(`/api/Clinic/Product?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.creating = false;
		}
	};

	updateClinicProduct = async (reqModel: any) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Clinic/Product?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	deleteClinicProduct = async (id: number) => {
		this.deleting = true;
		try {
			const res = await Axios.delete(`/api/Clinic/Product/${id}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.deleting = false;
		}
	};

	getClinicMemberProducts = async (id: number) => {
		this.loading = true;
		try {
			const res = await Axios.get(`/api/Clinic/Practitioner/Product/${id}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};

	deleteClinicMemberProduct = async (id: number) => {
		this.deleting = true;
		try {
			const res = await Axios.delete(`/api/Clinic/Practitioner/Product/${id}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.deleting = false;
		}
	};

	createClinicMemberProduct = async (reqModel: any) => {
		this.creating = true;
		try {
			const res = await Axios.post(`/api/Clinic/Practitioner/Product?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.creating = false;
		}
	};

	updateClinicMemberProduct = async (reqModel: any) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Clinic/Practitioner/Product?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	searchClinicMemberProduct = async (skip: number, take: number, reqModel: any) => {
		this.loading = true;
		try {
			const res = await Axios.post(`/api/Clinic/Practitioner/Product/Search/${skip}/${take}?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};

	updateClinicMemberInfo = async (reqModel: any) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Clinic/Practitioner?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	copyProducts = async (reqModel: any) => {
		this.updating = true;
		try {
			const res = await Axios.post(`/api/Clinic/Product/Copy?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	searchProducts = async (skip: number, take: number = PAGE_SIZE, reqModel: any = {}) => {
		this.loading = true;
		try {
			const res = await Axios.post(`/api/Clinic/Product/Search/${skip}/${take}?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};

	searchModalityByClinic = async (reqModel: any) => {
		this.searchingModality = true;
		try {
			const res = await Axios.post(`/api/Modality/Search/0/0?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.searchingModality = false;
		}
	};

	// The registered Hicaps terminal for this clinic — connectedDeviceIpAddr
	// is the terminal PC's own address (the hub connect URL is built from
	// it), serverIpAddr is the HICAPS server address each terminal call's
	// own serverUrl parameter needs. Both replace what used to be hardcoded
	// constants.
	searchPaymentDevice = async (clinicId: number) => {
		this.searchingPaymentDevice = true;
		try {
			const res = await Axios.post(`/api/Clinic/Device/Payment/Search/0/0?platformId=${CURRENT_PLATFORM}`, { clinicId });
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.searchingPaymentDevice = false;
		}
	};

}
